import { useState, useEffect } from "react";
import { IoIosArrowRoundBack } from "react-icons/io";
import { MdReceipt, MdPerson, MdClose } from "react-icons/md";
import { Link, useParams } from "react-router-dom";
import { RotatingLines } from "react-loader-spinner";
import { toast } from "react-toastify";
import { useAuth } from "../../../context/Auth";
import RoleBasedComponent from "../../../components/RoleBasedComponent";
import { showApiErrors } from "../../../utils/helpers";

export default function SupplierInvoiceDetail() {
  const { invoice_id } = useParams();
  const { requestWithAuth } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  const [showAmountModal, setShowAmountModal] = useState(false);
  const [invoiceAmount, setInvoiceAmount] = useState("");
  const [submittingAmount, setSubmittingAmount] = useState(false);

  const fetchInvoiceDetails = async () => {
    setLoading(true);
    try {
      const response = await requestWithAuth(
        `/supplier/invoice/${invoice_id}/details`,
        "GET"
      );
      setInvoiceData(response?.data);
    } catch (error) {
      toast.error("Error fetching invoice details");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAmount = async () => {
    if (!invoiceAmount || parseFloat(invoiceAmount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setSubmittingAmount(true);
    try {
      const body = JSON.stringify({
        invoice_amount: parseFloat(invoiceAmount),
      });

      await requestWithAuth(
        `/supplier/invoice/${invoice_id}/details/`,
        "PUT",
        body,
        {
          "Content-Type": "application/json",
        }
      );

      toast.success("Invoice amount updated successfully");
      setShowAmountModal(false);
      setInvoiceAmount("");
      fetchInvoiceDetails();
    } catch (error) {
      showApiErrors(error);
    } finally {
      setSubmittingAmount(false);
    }
  };

  useEffect(() => {
    fetchInvoiceDetails();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <RotatingLines
          visible={true}
          height="50"
          width="50"
          strokeColor="#4F46E5"
          strokeWidth="4"
          animationDuration="0.75"
          ariaLabel="rotating-lines-loading"
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MdReceipt className="h-8 w-8 text-white" />
                <h1 className="text-3xl font-bold text-white">
                  Invoice Details
                </h1>
              </div>
              <Link
                to="/inventory/invoice"
                className="bg-white/20 hover:bg-white/30 text-white px-6 py-2 rounded-lg transition-all duration-200 flex items-center space-x-2 font-semibold"
              >
                <IoIosArrowRoundBack className="h-6 w-6" />
                <span>Back to List</span>
              </Link>
            </div>
          </div>
        </div>

        {/* Invoice Information Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Invoice Details Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-slate-100">
              <div className="bg-indigo-100 p-2 rounded-lg">
                <MdReceipt className="text-indigo-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">
                Invoice Information
              </h2>
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-600">
                  Invoice Number:
                </span>
                <span className="text-sm font-bold text-slate-800">
                  {invoiceData?.invoice_number}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-600">
                  Invoice Date:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.invoice_date}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-600">
                  Recorded By:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.recorded_by}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-semibold text-slate-600">
                  Recorded Date:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.recorded_date}
                </span>
              </div>
              <div className="flex justify-between items-center py-2">
                <span className="text-sm font-semibold text-slate-600">
                  Invoice Amount:
                </span>
                {invoiceData?.invoice_amount ? (
                  <span className="text-sm text-slate-800">
                    {invoiceData?.invoice_amount}
                  </span>
                ) : (
                  <RoleBasedComponent allowedRoles={["superadmin", "admin"]}>
                    <button
                      onClick={() => setShowAmountModal(true)}
                      className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                      Set Amount
                    </button>
                  </RoleBasedComponent>
                )}
              </div>
              {invoiceData?.invoice_narration && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm font-semibold text-slate-600">
                    Narration:
                  </span>
                  <span className="text-sm text-slate-800">
                    {invoiceData?.invoice_narration}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Supplier Details Card */}
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
            <div className="flex items-center gap-3 mb-6 pb-4 border-b-2 border-slate-100">
              <div className="bg-green-100 p-2 rounded-lg">
                <MdPerson className="text-green-600 text-xl" />
              </div>
              <h2 className="text-xl font-bold text-slate-800">
                Supplier Information
              </h2>
            </div>
            <div className="space-y-4">
              <div className="py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-600 block mb-1">
                  Supplier Name:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.supplier}
                </span>
              </div>
              <div className="py-2 border-b border-slate-100">
                <span className="text-sm font-semibold text-slate-600 block mb-1">
                  Contact:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.supplier_contact}
                </span>
              </div>
              <div className="py-2">
                <span className="text-sm font-semibold text-slate-600 block mb-1">
                  Address:
                </span>
                <span className="text-sm text-slate-800">
                  {invoiceData?.supplier_address}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
          <div className="px-8 py-6 bg-gradient-to-r from-indigo-600 to-indigo-700">
            <h2 className="text-xl font-bold text-white">Invoice Items</h2>
          </div>
          <div className="p-8">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left py-4 px-6 font-semibold text-slate-700 border-b-2 border-slate-200">
                      #
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-slate-700 border-b-2 border-slate-200">
                      Drug Name
                    </th>
                    <th className="text-right py-4 px-6 font-semibold text-slate-700 border-b-2 border-slate-200">
                      Quantity
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-slate-700 border-b-2 border-slate-200">
                      Expiry Date
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceData?.items?.map((item, index) => (
                    <tr
                      key={item.id}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="py-4 px-6 border-b border-slate-100 text-slate-600">
                        {index + 1}
                      </td>
                      <td className="py-4 px-6 border-b border-slate-100 text-slate-800 font-medium">
                        {item.drug_name}
                      </td>
                      <td className="py-4 px-6 border-b border-slate-100 text-right text-slate-800">
                        {item.quantity}
                      </td>
                      <td className="py-4 px-6 border-b border-slate-100 text-slate-800">
                        {item.expiry_date}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Set Invoice Amount Modal */}
      {showAmountModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-900">
                Set Invoice Amount
              </h3>
              <button
                onClick={() => {
                  setShowAmountModal(false);
                  setInvoiceAmount("");
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <MdClose className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-1">
                  Invoice Number:{" "}
                  <span className="font-semibold">
                    {invoiceData?.invoice_number}
                  </span>
                </p>
                <p className="text-sm text-gray-600">
                  Supplier:{" "}
                  <span className="font-semibold">{invoiceData?.supplier}</span>
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Invoice Amount <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={invoiceAmount}
                  onChange={(e) => setInvoiceAmount(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                  placeholder="Enter amount"
                  autoFocus
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setShowAmountModal(false);
                  setInvoiceAmount("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAmount}
                disabled={submittingAmount || !invoiceAmount}
                className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
                  submittingAmount || !invoiceAmount
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-indigo-600 text-white hover:bg-indigo-700"
                }`}
              >
                {submittingAmount ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Submitting...
                  </span>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
